import { ReactNode, useMemo } from "react"
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { Card } from "../../common"
import { formatValue } from "../TableVisualization/utils"
import { formatNumber } from "~/ui-rtk/utils/format"
import VerticalLegend from "../VerticalLegend/VerticalLegend"
import useChartLoadingDataForAnimation from "~/ui-rtk/hooks/chart-loading-animation"

const CustomCursor = () => null

export default function BarChart({
  metrics,
  config,
  compareRange,
  chartId,
  bottomLegend = false,
  isLoading = false,
  layout = "horizontal",
}: {
  chartId?: string
  metrics: Record<string, number>
  compareRange?: Date[] | string[]
  config: {
    label: string
    color: string
    dataKey: string
    type?: string
    formatTooltip?: (metrics: Record<string, number>) => ReactNode
  }[]
  bottomLegend?: boolean
  isLoading?: boolean
  layout?: "horizontal" | "vertical"
}) {
  const safeConfig = config ?? []
  const compareMode = useMemo(() => !!compareRange, [compareRange])
  const data = useMemo(
    () =>
      safeConfig.map(chart => ({
        name: chart.label,
        value: +metrics?.[chart.dataKey],
        compareValue: compareMode
          ? +metrics?.[`compared.${chart.dataKey}`]
          : undefined,
        color: chart.color,
        type: chart.type,
        formatTooltip: chart.formatTooltip,
      })),
    [safeConfig, metrics],
  )

  const getGeneratedLoadingData = () =>
    safeConfig.map(chart => ({
      name: chart.label,
      value: Math.floor(Math.random() * 1000),
      color: chart.color,
      type: chart.type,
      formatTooltip: chart.formatTooltip,
    }))

  const loadingData = useChartLoadingDataForAnimation(
    isLoading,
    getGeneratedLoadingData,
  )

  return (
    <div className="h-full">
      <div className={`relative ${bottomLegend ? "h-3/4" : "h-full"}`}>
        {isLoading && (
          <div className="flex items-center justify-center absolute top-0 bottom-0 right-0 left-0 bg-[#00000066] z-50">
            <Card rounded className="p-5">
              Loading ...
            </Card>
          </div>
        )}
        <ResponsiveContainer width="100%" height="100%" minHeight={210}>
          <ReBarChart
            layout={layout}
            width={500}
            height={300}
            data={isLoading ? (loadingData as Record<string, unknown>[]) : data}
            margin={{
              top: 20,
              right: 0,
              left: layout === "vertical" ? 20 : 0,
              bottom: 0,
            }}
            style={{
              transform: "translate(-12px)",
            }}
          >
            <CartesianGrid
              vertical={layout === "vertical"}
              horizontal={layout !== "vertical"}
              strokeDasharray="0"
              stroke="#FFFFFF44"
            />
            {layout === "vertical" ? (
              <>
                <XAxis
                  type="number"
                  tickFormatter={(value, index) =>
                    index !== 0 || value > 0
                      ? `${formatNumber(value, { compact: true })}`
                      : ""
                  }
                  tick={{ fill: "#FFFFFFAA" }}
                  stroke="#FFFFFF44"
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: "#FFFFFFAA" }}
                  stroke="#FFFFFF44"
                  width={120}
                  interval={0}
                />
              </>
            ) : (
              <>
                {!bottomLegend && (
                  <XAxis
                    dataKey="name"
                    fill="#FFFFFF44"
                    tick={{ fill: "#FFFFFFAA" }}
                  />
                )}
                <YAxis
                  tickFormatter={(value, index) =>
                    index !== 0 || value > 0
                      ? `${formatNumber(value, { compact: true })}`
                      : ""
                  }
                  tick={{ fill: "#FFFFFFAA" }}
                  domain={[0, "auto"]}
                  stroke="#FFFFFF44"
                />
              </>
            )}

            <Tooltip
              cursor={<CustomCursor />}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const dataPoint = payload[0].payload
                  return (
                    <Card rounded className="px-system-md py-system-sm text-lg">
                      {dataPoint.formatTooltip
                        ? dataPoint.formatTooltip(metrics)
                        : formatValue(
                            dataPoint.value,
                            dataPoint.type,
                            undefined,
                          )}
                      {compareMode && (
                        <span className="text-sm">
                          {" "}
                          (Prev:{" "}
                          {formatValue(
                            dataPoint.compareValue,
                            dataPoint.type,
                            undefined,
                          )}
                          )
                        </span>
                      )}
                    </Card>
                  )
                }
                return null
              }}
            />
            <Bar dataKey="value" activeBar={false}>
              {data.map((item, idx) => (
                <Cell key={`cell-${idx}`} fill={item.color} />
              ))}
            </Bar>
            {compareMode ? (
              <>
                {data.map((item, idx) => (
                  <defs key={`def-${idx}`}>
                    <pattern
                      id={`line-${chartId}-${idx}`}
                      width="5"
                      height="5"
                      patternUnits="userSpaceOnUse"
                      patternTransform="rotate(45)"
                    >
                      <line
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="11"
                        strokeWidth="2"
                        stroke={item.color}
                      />
                    </pattern>
                  </defs>
                ))}
                <Bar dataKey="compareValue" activeBar={false}>
                  {data.map((item, idx) => (
                    <Cell
                      key={`cell-${idx}`}
                      fill={`url(#line-${chartId}-${idx})`}
                      stroke={item.color}
                    />
                  ))}
                </Bar>
              </>
            ) : null}
          </ReBarChart>
        </ResponsiveContainer>
      </div>
      {bottomLegend && (
        <div className="ps-system-2xl mt-system-sm">
          <VerticalLegend graphs={safeConfig} />
        </div>
      )}
    </div>
  )
}
